"use client";
import { contactInfo } from "@/lib/contacts";
import React, { useEffect, useState } from "react";
import { useModalCustomerContext } from "@/context/CustomerContext";
import { useTranslations } from "next-intl";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";

const Sidebar = () => {
	const [isVisible, setIsVisible] = useState<boolean>(false);
	const { openModalCustomer, isCustomerModalOpen } = useModalCustomerContext();
	const { openModalInquiry } = useModalInquiryContext();
	const t = useTranslations("nav");

	const scrollToTop = () => {
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	};

	const items = [
		{ 
			icon: "ri-phone-fill", 
			text: t("Phone"), 
			info: contactInfo.phone,
			link: `tel:${contactInfo.phone}`,
			type: "link"
		},
		{ 
			icon: "ri-whatsapp-fill", 
			text: t("WhatsApp"), 
			info: contactInfo.whatsapp,
			link: `https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, "")}`,
			type: "link"
		},
		{
			icon: "ri-mail-fill",
			text: t("Email"),
			info: contactInfo.email,
			link: `mailto:${contactInfo.email}`,
			type: "link"
		},
		{
			icon: "ri-wechat-fill",
			text: t("Wechat"),
			info: contactInfo.wechat,
			link: `https://web.weixin.qq.com/`,
			type: "link"
		},
		{ 
			icon: "ri-customer-service-fill", 
			text: t("Customer-Service"), 
			info: "",
			link: "#",
			type: "customer"
		},
		{ 
			icon: "ri-file-list-2-line", 
			text: t("Inquiry"), 
			info: "",
			link: "#",
			type: "inquiry"
		},
		{ 
			icon: "ri-arrow-up-circle-fill", 
			text: t("Back-to-Top"), 
			info: "",
			link: "#",
			type: "scroll"
		},
	];
 

	// 添加滚动监听器
	useEffect(() => {
		const handleScroll = () => {
			const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
			if (scrollTop > 200) {
				setIsVisible(true);
			} else {
				setIsVisible(false);
			}
		};

		window.addEventListener("scroll", handleScroll);

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);
	const handleItemClick = (item: any, e: React.MouseEvent) => {
		if (item.type === "scroll") {
			e.preventDefault();
			scrollToTop();
		} else if (item.type === "customer") {
			e.preventDefault();
			openModalCustomer();
		} else if (item.type === "inquiry") {
			e.preventDefault();
			openModalInquiry(false);
		} else if (item.type === "wechat") {
			e.preventDefault();
			window.open("https://web.weixin.qq.com/", "_blank");
		}
	};

	return isVisible && !isCustomerModalOpen ? (
		<div className="fixed right-6 lg:right-8 top-1/2 -translate-y-1/2 z-50 max-md:hidden">
			<div className="group relative flex items-center">
				{/* hover时显示的SliderBar内容 - 从右侧滑入 */}
				<div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 translate-x-8 scale-95 group-hover:opacity-100 group-hover:translate-x-0 group-hover:scale-100 transition-all duration-500 ease-out pointer-events-none group-hover:pointer-events-auto z-20">
					<div className="flex flex-col">
						{items.map((item, index) => (
							<div
								key={index}
								className="group/item relative mb-2.5 opacity-0 translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 ease-out"
								style={{
									transitionDelay: `${index * 0.05}s`
								}}
							>
								{item.type === "link" ? (
									<>
										{/* 联系方式 - hover时从右侧向左延伸 */}
										{item.info && (
											<div className="rounded-tl-full rounded-bl-full absolute right-full top-0 h-[68px] flex items-center px-6 py-2.5 bg-mainColor text-white whitespace-nowrap z-10 shadow-lg w-0 opacity-0 pointer-events-none group-hover/item:w-[260px] group-hover/item:opacity-100 group-hover/item:pointer-events-auto transition-all duration-500 ease-out mr-2.5">
												<span className="text-[17px] font-medium opacity-0 group-hover/item:opacity-100 transition-opacity duration-300 delay-200 w-full">{item.info}</span>
											</div>
										)}
										{/* 按钮本身 */}
										<a
											href={item.link}
											target="_blank"
											rel="noopener noreferrer"
											aria-label={`${item.text}: ${item.info}`}
											className="flex items-center justify-center w-[68px] h-[68px] bg-black/30 hover:bg-mainColor cursor-pointer transition-all duration-300 relative z-30 rounded-full shadow-md hover:shadow-lg hover:scale-110 active:scale-95"
										>
											<i className={`${item.icon} text-[34px] text-white transition-all duration-300`} aria-hidden="true"></i>
										</a>
									</>
								) : (
									<button
										type="button"
										onClick={(e) => handleItemClick(item, e)}
										aria-label={item.text}
										className="flex items-center justify-center w-[68px] h-[68px] bg-black/30 hover:bg-mainColor cursor-pointer transition-all duration-300 rounded-full shadow-md hover:shadow-lg hover:scale-110 active:scale-95 relative z-30"
									>
										<i className={`${item.icon} text-[34px] text-white transition-all duration-300`} aria-hidden="true"></i>
									</button>
								)}
							</div>
						))}
					</div>
				</div>
				
				{/* 默认按钮 - hover时逐渐消失 */}
				<button
					type="button"
					aria-label={"Open contact menu"}
					className="flex items-center justify-center w-[68px] h-[68px] bg-black/30 hover:bg-mainColor cursor-pointer transition-all duration-500 ease-out rounded-full shadow-lg relative z-10 group-hover:opacity-0 group-hover:scale-75 group-hover:pointer-events-none"
				>
					<i className="ri-menu-line text-[34px] text-white transition-transform duration-500 group-hover:rotate-180" aria-hidden="true"></i>
				</button>
				
				{/* 扩大hover区域，连接按钮和SliderBar，防止鼠标移动时消失 - 不拦截点击事件 */}
				<div className="absolute right-0 top-1/2 -translate-y-1/2 w-[290px] h-[700px] pointer-events-none z-0"></div>
			</div>
		</div>
	) : null;
};

export default React.memo(Sidebar);
